import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Translate} from 'react-redux-i18n';
import {useLocation} from 'react-router-dom';
import {parse} from 'query-string';

import * as notificationsActions from '../../../actions/notifications';
import * as snackbarsActions from '../../../actions/snackbars';
import interventionService from '../../../services/intervention/InterventionService';
import {interventionRole, PROJECT_STATUS, notificationType} from '../../../utils/constant';
import {captureError} from '../../../utils/log';
import {goToProject} from '../../../utils/project';
import InvitationRejectionReason from '../../InvitationRejectionReason';
import ReceivedInvitationsView from '../../notification/ReceivedInvitationsView';

function InvitationsContainer() {
  const organisation = useSelector(globalState => globalState.organisation);
  const invitations = useSelector(globalState => globalState.notifications.notificationsWithActions
    .filter(notification => notification.type === notificationType.PROJECT_NOT_AUTOMATICALLY_ACCEPTED));
  const globalDispatch = useDispatch();
  const location = useLocation();
  const [invitationToReject, setInvitationToReject] = useState(null);

  function refreshInvitations() {
    globalDispatch(notificationsActions.fetchNotifications(organisation));
  }

  function acceptInvitation(intervention) {
    return new Promise((resolve, reject) => {
      interventionService.accept(intervention.interventionId)
        .then(response => resolve(response))
        .catch(error => {
          captureError('Error while accepting an intervention', error);
          globalDispatch(snackbarsActions.addError(<Translate value="error.generic"/>));
          refreshInvitations();
          reject();
        });
    });
  }

  function handleAcceptInvitation(notification) {
    acceptInvitation(notification.intervention)
      .then(() => {
        globalDispatch(notificationsActions.dismissNotification(notification.notificationId));
        goToProject(notification.intervention.projectId, notification.intervention.interventionId);
      });
  }

  function handleSubmitSuccess(response) {
    if (!invitationToReject.isFromQuery) {
      globalDispatch(notificationsActions.dismissNotification(invitationToReject.notificationId));
      return;
    }

    if (response.status === 200) {
      globalDispatch(snackbarsActions.addSuccess(<Translate value="project.projectRejected"/>));
    } else if (response.status === 204) {
      globalDispatch(snackbarsActions.addError(<Translate value="project.projectAlreadyReplied"/>));
    }

    refreshInvitations();
  }

  function handleSubmitError() {
    globalDispatch(snackbarsActions.addError(<Translate value="error.generic"/>));
    refreshInvitations();
  }

  function handleInvitationFromUrl() {
    const queryParams = parse(location.search);
    const interventionId = Number(queryParams.acceptedInterventionId) || Number(queryParams.rejectedInterventionId);

    if (!interventionId) {
      return;
    }

    const isAcceptation = Number(queryParams.acceptedInterventionId) === interventionId;

    interventionService.getIntervention(interventionId)
      .then(intervention => {
        if (!intervention) {
          globalDispatch(snackbarsActions.addError(<Translate value="project.projectInviterRemoved"/>));
          return;
        }

        if (!organisation.depositor &&
          (intervention.role === interventionRole.SUB_CONTRACTOR || intervention.role === interventionRole.GENERAL_CONTRACTOR)) {
          globalDispatch(snackbarsActions.addError(<Translate value="relationship.errorDepositorService"/>));
          return;
        }

        if (intervention.project.projectStatus === PROJECT_STATUS.CLOSED) {
          globalDispatch(snackbarsActions.addError(<Translate value="project.projectAlreadyClosed"/>));
          return;
        }

        if (isAcceptation) {
          acceptInvitation(intervention)
            .then(response => {
              if (response.status === 200) {
                globalDispatch(snackbarsActions.addSuccess(<Translate value="project.projectAccepted"/>));
                refreshInvitations();
                goToProject(intervention.project.projectId, intervention.interventionId);
              } else if (response.status === 204) {
                globalDispatch(snackbarsActions.addError(<Translate value="project.projectAlreadyReplied"/>));
              }
            });
        } else {
          setInvitationToReject({intervention: {interventionId}, isFromQuery: true});
        }
      })
      .catch(error => captureError('Error while getting intervention details', error));
  }

  React.useEffect(() => {
    refreshInvitations();
    handleInvitationFromUrl();
  }, []);

  return (
    <>
      <ReceivedInvitationsView
        invitations={invitations}
        onAcceptInvitation={handleAcceptInvitation}
        onOpenRejectionReasonModal={invitation => setInvitationToReject(invitation)}
      />

      {invitationToReject &&
        <InvitationRejectionReason
          onClose={() => setInvitationToReject(null)}
          invitation={invitationToReject}
          onSubmitSuccess={handleSubmitSuccess}
          onSubmitError={handleSubmitError}
        />
      }
    </>
  );
}

export default InvitationsContainer;
